// Generated from api/openapi/operations.yaml by make docs. Do not edit.
import { z } from "zod";

export const dashboardStatusSchema = z.enum(["compliant", "partial", "non_compliant", "not_applicable", "not_assessed"]);

export const severitySchema = z.enum(["critical", "high", "medium", "low"]);

export const actorScopeSchema = z.enum(["tenant", "tenant_grant", "platform"]);

export const requestStatusSchema = z.enum(["pending", "approved", "rejected", "lapsed", "withdrawn"]);

export const coverageIndexSchema = z.object({
  "index": z.number().int().min(0).max(100),
  "assessed": z.number().int().min(0),
  "applicable": z.number().int().min(0),
  "total": z.number().int().min(0)
});

export const dashboardCountsSchema = z.object({
  "design_only": z.number().int().min(0),
  "attested_met": z.number().int().min(0),
  "total": z.number().int().min(0),
  "compliant": z.number().int().min(0),
  "partial": z.number().int().min(0),
  "non_compliant": z.number().int().min(0),
  "not_applicable": z.number().int().min(0),
  "not_assessed": z.number().int().min(0),
  "findings": z.number().int().min(0),
  "findings_needing_attention": z.number().int().min(0)
});

export const confidenceCountsSchema = z.object({
  "measured": z.number().int().min(0),
  "by_design": z.number().int().min(0),
  "attested": z.number().int().min(0),
  "operator_decision": z.number().int().min(0),
  "not_checked": z.number().int().min(0)
});

export const treatmentCountsSchema = z.object({
  "exception": z.number().int().min(0),
  "not_applicable": z.number().int().min(0)
});

export const dashboardFrameworkSchema = z.object({
  "framework_id": z.string(),
  "framework_version": z.string(),
  "name": z.string(),
  "counts": dashboardCountsSchema,
  "coverage_index": coverageIndexSchema,
  "by_confidence": confidenceCountsSchema,
  "instrument_total": z.number().int().min(1).optional(),
  "subset_rationale": z.string().optional(),
  "recent_trend": z.array(z.number().int().min(0).max(100)).optional(),
  "trend_delta": z.number().int(),
  "health": z.enum(["good", "warning", "critical"])
});

export const dashboardTopIssueSchema = z.object({
  "catalog_id": z.string().uuid(),
  "framework_id": z.string(),
  "framework_version": z.string(),
  "control_id": z.string(),
  "title": z.string(),
  "family": z.string(),
  "severity": severitySchema,
  "status": dashboardStatusSchema,
  "summary": z.string(),
  "remediation": z.string().optional()
});

export const dashboardHeatmapCellSchema = z.object({
  "family": z.string(),
  "framework_id": z.string(),
  "dominant_status": dashboardStatusSchema,
  "control_count": z.number().int().min(0),
  "finding_count": z.number().int().min(0)
});

export const dashboardActiveExceptionSchema = z.object({
  "catalog_id": z.string().uuid(),
  "framework_id": z.string(),
  "framework_version": z.string(),
  "control_id": z.string(),
  "title": z.string(),
  "severity": severitySchema,
  "scope_note": z.string().optional(),
  "compensating_control": z.string().optional(),
  "expires_at": z.string().datetime({ offset: true }),
  "override_set_at": z.string().datetime({ offset: true })
});

export const reportEvidenceRefSchema = z.object({
  "catalog_id": z.string().uuid(),
  "framework_id": z.string(),
  "framework_version": z.string(),
  "control_id": z.string(),
  "title": z.string()
});

export const refreshStatusSchema = z.object({
  "next_run_at": z.string().datetime({ offset: true }),
  "priority": z.string(),
  "reason": z.string(),
  "in_flight": z.boolean(),
  "claimed_by": z.string().optional(),
  "claimed_at": z.string().datetime({ offset: true }).optional(),
  "last_completed_at": z.string().datetime({ offset: true }).optional(),
  "last_failed_at": z.string().datetime({ offset: true }).optional(),
  "last_duration_ms": z.number().int().optional(),
  "last_error": z.string().optional(),
  "requested_by": z.string().uuid().optional()
});

export const dashboardSummarySchema = z.object({
  "tenant_id": z.string().uuid(),
  "generated_at": z.string().datetime({ offset: true }),
  "coverage_index": coverageIndexSchema,
  "counts": dashboardCountsSchema,
  "by_confidence": confidenceCountsSchema,
  "treatments": treatmentCountsSchema,
  "frameworks": z.array(dashboardFrameworkSchema),
  "top_issues": z.array(dashboardTopIssueSchema).max(5),
  "heatmap": z.array(dashboardHeatmapCellSchema),
  "crosswalk": z.array(z.object({
    "source_framework_id": z.string(),
    "target_framework_id": z.string(),
    "source_controls": z.number().int().min(0),
    "mapped_controls": z.number().int().min(0),
    "coverage": z.number().int().min(0).max(100)
  })),
  "active_exceptions": z.array(dashboardActiveExceptionSchema),
  "report_evidence": z.record(z.string(), z.array(reportEvidenceRefSchema)).optional(),
  "refresh_status": refreshStatusSchema
});

export const findingsPageSchema = z.object({
  "items": z.array(dashboardTopIssueSchema),
  "next_cursor": z.string().optional(),
  "total": z.number().int().min(0)
});

export const trendPointSchema = z.object({
  "at": z.string().datetime({ offset: true }),
  "score": z.number().int().min(0).max(100),
  "counts": dashboardCountsSchema
});

export const trendSeriesSchema = z.object({
  "points": z.array(trendPointSchema).min(1),
  "current_score": z.number().int().min(0).max(100),
  "window_start": z.string().datetime({ offset: true }),
  "window_end": z.string().datetime({ offset: true }),
  "score_delta": z.number().int(),
  "oldest_observed": z.string().datetime({ offset: true }).optional()
});

export const controlHistoryEntrySchema = z.object({
  "snapshot_id": z.string().uuid(),
  "status": dashboardStatusSchema,
  "previous_status": z.string().optional(),
  "captured_at": z.string().datetime({ offset: true }),
  "captured_reason": z.enum(["state_change", "evidence_change", "override_change", "manual_review", "evaluator_upgrade"]),
  "captured_by_email": z.string().optional(),
  "evaluator_version": z.string().optional(),
  "summary": z.string().optional(),
  "row_hash": z.string()
});

export const controlHistoryResponseSchema = z.object({
  "data": z.array(controlHistoryEntrySchema)
});

export const checkStatusSchema = z.enum(["pass", "warn", "fail"]);

export const readinessCheckSchema = z.object({
  "id": z.string(),
  "title": z.string(),
  "description": z.string(),
  "status": checkStatusSchema,
  "severity": z.enum(["blocker", "advisory"]),
  "detail": z.string().optional(),
  "remediation": z.string().optional(),
  "affected_control_ids": z.array(z.string().uuid()).optional()
});

export const readinessReportSchema = z.object({
  "framework_id": z.string(),
  "framework_version": z.string(),
  "name": z.string(),
  "generated_at": z.string().datetime({ offset: true }),
  "overall_status": checkStatusSchema,
  "overall_narrative": z.string(),
  "blocker_count": z.number().int().min(0),
  "advisory_count": z.number().int().min(0),
  "pass_count": z.number().int().min(0),
  "checks": z.array(readinessCheckSchema)
});

export const questionnaireRowSchema = z.object({
  "id": z.string(),
  "question": z.string(),
  "answer": z.string(),
  "evidence": z.string().optional(),
  "confidence": z.enum(["auto", "asserted_unverified", "partial", "manual_input_required"])
});

export const questionnaireSectionSchema = z.object({
  "id": z.string(),
  "title": z.string(),
  "questions": z.array(questionnaireRowSchema)
});

export const questionnaireResponseSchema = z.object({
  "format": z.enum(["caiq_v4", "sig_lite"]),
  "tenant_id": z.string().uuid(),
  "generated_at": z.string().datetime({ offset: true }),
  "questions_total": z.number().int().min(0),
  "questions_answered": z.number().int().min(0),
  "sections": z.array(questionnaireSectionSchema),
  "disclaimer": z.string()
});

export const counterSignatureSchema = z.object({
  "counter_signed_by_email": z.string(),
  "counter_signed_at": z.string().datetime({ offset: true }),
  "content_sha256": z.string()
});

export const attestationRequestSchema = z.object({
  "id": z.string().uuid(),
  "catalog_id": z.string().uuid(),
  "framework_id": z.string(),
  "framework_version": z.string(),
  "control_id": z.string(),
  "control_title": z.string(),
  "statement": z.string(),
  "expires_at": z.string().datetime({ offset: true }),
  "attested_by_email": z.string(),
  "attested_by_scope": actorScopeSchema.optional(),
  "attested_at": z.string().datetime({ offset: true }),
  "reviewer_email": z.string(),
  "status": requestStatusSchema,
  "decided_by_email": z.string().optional(),
  "decided_by_scope": actorScopeSchema.optional(),
  "decided_at": z.string().datetime({ offset: true }).optional(),
  "decision_note": z.string().optional(),
  "artefact_count": z.number().int(),
  "artefacts": z.array(z.object({
    "id": z.string().uuid(),
    "filename": z.string(),
    "sha256": z.string()
  }))
});

export const attestationRequestListSchema = z.object({
  "data": z.array(attestationRequestSchema)
});

export const pendingCloseSchema = z.object({
  "id": z.string().uuid(),
  "period_start": z.string().datetime({ offset: true }),
  "period_end": z.string().datetime({ offset: true }),
  "manifest_sha256": z.string(),
  "requested_by_email": z.string(),
  "requested_by_role": z.string(),
  "requested_by_scope": actorScopeSchema.optional(),
  "requested_at": z.string().datetime({ offset: true }),
  "reviewer_email": z.string(),
  "status": requestStatusSchema,
  "decided_by_email": z.string().optional(),
  "decided_by_scope": actorScopeSchema.optional(),
  "decided_at": z.string().datetime({ offset: true }).optional(),
  "decision_note": z.string().optional()
});

export const pendingCloseListSchema = z.object({
  "data": z.array(pendingCloseSchema)
});

export const periodCloseSchema = z.object({
  "id": z.string().uuid(),
  "period_start": z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  "period_end": z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  "closed_by_email": z.string(),
  "closed_by_role": z.string(),
  "closed_by_scope": actorScopeSchema.optional(),
  "reviewed_by_email": z.string(),
  "reviewed_by_role": z.string(),
  "reviewed_by_scope": actorScopeSchema.optional(),
  "provider_only": z.boolean(),
  "manifest_sha256": z.string(),
  "signature": z.string(),
  "closed_at": z.string().datetime({ offset: true })
});

export const periodCloseListSchema = z.object({
  "data": z.array(periodCloseSchema)
});

export const transparencyWitnessSchema = z.object({
  "id": z.string().uuid(),
  "name": z.string(),
  "key_id": z.string(),
  "registered_by_email": z.string(),
  "registered_at": z.string().datetime({ offset: true }),
  "revoked_at": z.string().datetime({ offset: true }).optional(),
  "revoked_by_email": z.string().optional(),
  "last_tree_size": z.number().int().min(0).optional(),
  "last_witnessed_at": z.string().datetime({ offset: true }).optional()
});

export const transparencyWitnessListSchema = z.object({
  "data": z.array(transparencyWitnessSchema),
  "log_signing_key_id": z.string()
});

export const auditorTokenSchema = z.object({
  "id": z.string().uuid(),
  "tenant_id": z.string().uuid(),
  "description": z.string(),
  "status": z.enum(["active", "revoked"]),
  "expires_at": z.string().datetime({ offset: true }),
  "last_used_at": z.string().datetime({ offset: true }).optional(),
  "use_count": z.number().int().min(0),
  "created_by": z.string().uuid().optional(),
  "created_at": z.string().datetime({ offset: true }),
  "revoked_at": z.string().datetime({ offset: true }).optional(),
  "owner_admin_id": z.string().uuid().optional(),
  "token": z.string().optional()
});

export const auditorTokenListSchema = z.object({
  "data": z.array(auditorTokenSchema)
});

export const auditorAccessLogEntrySchema = z.object({
  "id": z.string().uuid(),
  "token_id": z.string().uuid().optional(),
  "method": z.string(),
  "path": z.string(),
  "response_status": z.number().int(),
  "ip_address": z.string().optional(),
  "user_agent": z.string().optional(),
  "created_at": z.string().datetime({ offset: true })
});

export const auditorAccessLogListSchema = z.object({
  "data": z.array(auditorAccessLogEntrySchema)
});

export const mandateMeasureSchema = z.enum(["privileged_phishing_resistant", "otp_not_accepted", "phishing_resistant_default", "control"]);

export const mandateClauseSchema = z.object({
  "control_evaluators": z.array(z.string()).optional(),
  "id": z.string(),
  "in_instrument": z.boolean(),
  "measure": mandateMeasureSchema,
  "source": z.string(),
  "title": z.string()
});

export const mandateRuleSetSchema = z.object({
  "clauses": z.array(mandateClauseSchema),
  "counsel_rows": z.array(z.number().int()),
  "id": z.string(),
  "instruments": z.array(z.string()),
  "name": z.string(),
  "region": z.string()
});

export const mandateRuleSetsResponseSchema = z.object({
  "data": z.array(mandateRuleSetSchema)
});
